import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Search,
  Plus,
  Trash2,
  Share2,
  GalleryVerticalEnd,
  X,
  // UsersRound,
  CheckCheck,
  Calendar,
  Filter,
} from 'lucide-react';
import { Conversation } from '@/types';
import DatePicker from '@/components/DatePicker';
import ConfirmDialog from '@/components/ConfirmDialog';
import DeleteConversationDialog from '@/components/DeleteConversationDialog';
import ConversationListPanel from '@/components/ConversationListPanel';
import ShareModal from '@/components/ShareModal';
import BatchShareModal from '@/components/BatchShareModal';
import { showToast } from '@/components/Toast';
import RenameDialog from '@/components/Sidebar/RenameDialog';
import { useConversationActions } from '@/hooks/conversation/useConversationActions';
import { useProjectsStore } from '@/stores/projectsStore';
import { useConversationsStore } from '@/stores/conversationsStore';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { apiDelete, apiGet, apiPost } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import MobileHeader from '@/components/MobileHeader';

// XSS validation: check for dangerous HTML/script characters
const containsXSS = (text: string): boolean => {
  const xssPattern = /<[^>]*>|javascript:|on\w+\s*=|&#|&lt;|&gt;/i;
  return xssPattern.test(text);
};

// Hook to force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => {
      i18nInstance.off('languageChanged', onLanguageChanged);
    };
  }, []);
};

interface ChatsPageProps {
  onConversationSelect?: (conversationId: string) => void;
  onNewChat?: () => void;
}

export default function ChatsPage({
  onConversationSelect,
  onNewChat,
}: ChatsPageProps) {
  const { t } = useTranslation();
  useForceUpdate();

  // Tab state from UI store - use store value directly as single source of truth
  const activeTab = useUIStore(state => state.chatsListInitialTab);
  const setChatsListInitialTab = useUIStore(
    state => state.setChatsListInitialTab
  );
  // 🔥 新增：获取 activeStreamingMessages 用于显示对话的 loading 状态
  const activeStreamingMessages = useUIStore(
    state => state.activeStreamingMessages
  );

  // Get current user info for owner identification in group chats
  const userInfo = useAuthStore(state => state.userInfo);

  // Group chats state
  const [groupConversations, setGroupConversations] = useState<Conversation[]>(
    []
  );
  const [groupTotalCount, setGroupTotalCount] = useState<number>(0);
  const [isGroupLoading, setIsGroupLoading] = useState(false);
  const [groupPage, setGroupPage] = useState(1);
  const [hasMoreGroups, setHasMoreGroups] = useState(true);
  const groupFetchedRef = useRef<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // 🔥 时间筛选状态
  const [showTimePanel, setShowTimePanel] = useState(false);
  const [timeFilterType, setTimeFilterType] = useState<
    'updatedAt' | 'createdAt'
  >('updatedAt');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const timePanelRef = useRef<HTMLDivElement>(null);
  // 🔥 已应用的时间筛选（只在 Apply/Search 时更新，避免选择日期就自动触发查询）
  const appliedTimeRef = useRef<{
    type: 'updatedAt' | 'createdAt';
    param: string | null;
  }>({
    type: 'updatedAt',
    param: null,
  });

  // 🔥 构建时间筛选参数字符串
  const buildTimeParam = useCallback((): string | null => {
    if (!startDate && !endDate) return null;
    const start = startDate ? `${startDate}T00:00:00` : '';
    const end = endDate ? `${endDate}T23:59:59` : '';
    return `${start}--${end}`;
  }, [startDate, endDate]);

  // 🔥 快速选择时间范围
  const handleQuickSelect = useCallback(
    (range: 'lastWeek' | 'thisWeek' | 'lastMonth' | 'thisMonth') => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dayOfWeek = today.getDay() || 7; // 周日为7

      let start: Date;
      let end: Date;

      switch (range) {
        case 'thisWeek':
          start = new Date(today);
          start.setDate(today.getDate() - dayOfWeek + 1); // 本周一
          end = new Date(today); // 今天
          break;
        case 'lastWeek':
          start = new Date(today);
          start.setDate(today.getDate() - dayOfWeek - 6); // 上周一
          end = new Date(today);
          end.setDate(today.getDate() - dayOfWeek); // 上周日
          break;
        case 'thisMonth':
          start = new Date(today.getFullYear(), today.getMonth(), 1); // 本月1号
          end = new Date(today); // 今天
          break;
        case 'lastMonth':
          start = new Date(today.getFullYear(), today.getMonth() - 1, 1); // 上月1号
          end = new Date(today.getFullYear(), today.getMonth(), 0); // 上月最后一天
          break;
      }

      const fmt = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      setStartDate(fmt(start));
      setEndDate(fmt(end));
    },
    []
  );

  // 🔥 点击外部关闭时间面板
  useEffect(() => {
    if (!showTimePanel) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        timePanelRef.current &&
        !timePanelRef.current.contains(e.target as Node)
      ) {
        setShowTimePanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTimePanel]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveFromMenuId, setMoveFromMenuId] = useState<string | null>(null); // 从右键菜单发起移动时存储的单个会话 ID
  const [moveSearchQuery, setMoveSearchQuery] = useState('');
  const [selectedMoveProjectId, setSelectedMoveProjectId] = useState<
    string | null
  >(null);
  const [isMoving, setIsMoving] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameConversationId, setRenameConversationId] = useState<
    string | null
  >(null);
  const [renameValue, setRenameValue] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConversationId, setDeleteConversationId] = useState<
    string | null
  >(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showRemoveFromProjectModal, setShowRemoveFromProjectModal] =
    useState(false);
  const [removeFromProjectConversationId, setRemoveFromProjectConversationId] =
    useState<string | null>(null);
  const [isRemovingFromProject, setIsRemovingFromProject] = useState(false);
  const [shareModal, setShareModal] = useState<{
    isOpen: boolean;
    conversationId: string;
    conversationTitle: string;
  }>({
    isOpen: false,
    conversationId: '',
    conversationTitle: '',
  });

  // 批量分享状态
  const [batchShareModal, setBatchShareModal] = useState<{
    isOpen: boolean;
    conversationIds: string[];
    conversationTitles: string[];
  }>({
    isOpen: false,
    conversationIds: [],
    conversationTitles: [],
  });
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  // Pagination and data state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;

  const projects = useProjectsStore(state => state.projects);
  const setSidebarProjectConversationsCache = useConversationsStore(
    state => state.setSidebarProjectConversationsCache
  );
  const removeConversation = useConversationsStore(
    state => state.removeConversation
  );

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // 🔥 暴露函数到 window，用于侧边栏 star/unstar 后同步更新本地 conversations 状态
  useEffect(() => {
    window.updateConversationStarInPages = (
      conversationId: string,
      starred: boolean
    ) => {
      setConversations(prev =>
        prev.map(c =>
          c.conversationId === conversationId || c.id === conversationId
            ? { ...c, starred }
            : c
        )
      );
    };
    return () => {
      delete window.updateConversationStarInPages;
    };
  }, []);

  // 🔥 暴露函数到 window，用于侧边栏 rename 后同步更新本地 conversations 状态
  useEffect(() => {
    window.updateConversationTitleInPages = (
      conversationId: string,
      title: string
    ) => {
      setConversations(prev =>
        prev.map(c =>
          c.conversationId === conversationId || c.id === conversationId
            ? { ...c, title }
            : c
        )
      );
    };
    return () => {
      delete window.updateConversationTitleInPages;
    };
  }, []);

  // 🔥 暴露函数到 window，用于侧边栏 delete 后同步更新本地 conversations 状态
  useEffect(() => {
    window.removeConversationFromPages = (conversationId: string) => {
      setConversations(prev =>
        prev.filter(
          c => c.conversationId !== conversationId && c.id !== conversationId
        )
      );
    };
    return () => {
      delete window.removeConversationFromPages;
    };
  }, []);

  // Fetch conversations from API
  const fetchConversations = useCallback(
    async (pageNum: number, search?: string, reset: boolean = false) => {
      setIsLoading(true);
      try {
        const params: Record<string, string | number | boolean> = {
          page: pageNum,
          pageSize,
          all: true,
          conversationType: activeTab === 'chats' ? 'single' : 'group',
          allProjects: true,
        };
        if (search?.trim()) {
          params.title = search.trim();
        }
        // 🔥 从 appliedTimeRef 读取已应用的时间筛选参数（只在 Apply/Search 时更新）
        const { type: appliedType, param: appliedParam } =
          appliedTimeRef.current;
        if (appliedParam) {
          params[appliedType] = appliedParam;
        }
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(k =>
          searchParams.append(k, String((params as any)[k]))
        );
        const res = await apiGet<any>(
          `/api/ai/conversations?${searchParams.toString()}`
        );
        const data = res?.data || res || {};
        const newConversations = Array.isArray(data.conversations)
          ? data.conversations.map((conv: any) => ({
              ...conv,
              id: conv.conversationId,
              conversationType: conv.conversationType || 'single',
            }))
          : [];
        if (data.totalCount != null) {
          setTotalCount(data.totalCount);
        }
        if (reset || pageNum === 1) {
          setConversations(newConversations);
        } else {
          setConversations(prev => [...prev, ...newConversations]);
        }

        // Check if there are more items
        setHasMore(newConversations.length >= pageSize);
      } catch (error) {
        showToast(t('errors.failedToFetchConversations'), 'error', {
          title: t('common.error'),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize, t, activeTab]
  );

  // Initial load and search
  useEffect(() => {
    // Skip if already fetched with same query (prevents StrictMode double fetch)
    const fetchKey = `fetch-${debouncedSearchQuery}`;
    if (fetchedRef.current === fetchKey) {
      return;
    }
    fetchedRef.current = fetchKey;

    const doFetch = async () => {
      setPage(1);
      setIsLoading(true);
      try {
        const params: Record<string, string | number | boolean> = {
          page: 1,
          pageSize,
          allProjects: true,
          conversationType: 'single',
        };
        if (debouncedSearchQuery?.trim()) {
          params.title = debouncedSearchQuery.trim();
        }
        // 🔥 从 appliedTimeRef 读取已应用的时间筛选参数
        const { type: appliedType, param: appliedParam } =
          appliedTimeRef.current;
        if (appliedParam) {
          params[appliedType] = appliedParam;
        }
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(k =>
          searchParams.append(k, String((params as any)[k]))
        );
        const res = await apiGet<any>(
          `/api/ai/conversations?${searchParams.toString()}`
        );
        const data = res?.data || res || {};
        const newConversations = Array.isArray(data.conversations)
          ? data.conversations
          : [];
        if (data.totalCount != null) {
          setTotalCount(data.totalCount);
        }
        setConversations(newConversations);
        setHasMore(newConversations.length >= pageSize);
      } catch (error) {
        showToast(t('errors.failedToFetchConversations'), 'error', {
          title: t('common.error'),
        });
      } finally {
        setIsLoading(false);
      }
    };

    doFetch();
  }, [debouncedSearchQuery, pageSize, t]);

  // Refresh conversations
  const refreshConversations = useCallback(() => {
    setPage(1);
    return fetchConversations(1, debouncedSearchQuery, true);
  }, [debouncedSearchQuery, fetchConversations]);

  // Handle tab change
  const handleTabChange = useCallback(
    (tab: 'chats' | 'groupChats') => {
      setChatsListInitialTab(tab);
      setSearchQuery('');
      setSelectedIds(new Set());
    },
    [setChatsListInitialTab]
  );

  // Fetch group conversations
  const fetchGroupChatsData = useCallback(
    async (pageNum: number, search?: string, reset: boolean = false) => {
      setIsGroupLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('page', String(pageNum));
        params.append('pageSize', String(pageSize));
        params.append('allProjects', 'true');
        params.append('conversationType', 'group');
        if (search?.trim()) {
          params.append('title', search.trim());
        }
        // 🔥 从 appliedTimeRef 读取已应用的时间筛选参数
        const { type: appliedType, param: appliedParam } =
          appliedTimeRef.current;
        if (appliedParam) {
          params.append(appliedType, appliedParam);
        }
        const res = await apiGet<any>(
          `/api/ai/conversations?${params.toString()}`
        );
        const data = res?.data || res || {};
        const newConversations = Array.isArray(data.conversations)
          ? data.conversations.map((conv: any) => ({
              ...conv,
              id: conv.conversationId,
              conversationType: 'group',
            }))
          : [];
        if (data.totalCount != null) {
          setGroupTotalCount(data.totalCount);
        }
        if (reset || pageNum === 1) {
          setGroupConversations(newConversations);
        } else {
          setGroupConversations(prev => [...prev, ...newConversations]);
        }
        setHasMoreGroups(newConversations.length >= pageSize);
      } catch (error) {
        showToast(t('errors.failedToFetchGroupConversations'), 'error', {
          title: t('common.error'),
        });
      } finally {
        setIsGroupLoading(false);
      }
    },
    [pageSize, t]
  );

  // Initial fetch for group chats when tab is groupChats
  useEffect(() => {
    if (activeTab !== 'groupChats') return;

    const fetchKey = `group-fetch-${debouncedSearchQuery}`;
    if (groupFetchedRef.current === fetchKey) {
      return;
    }
    groupFetchedRef.current = fetchKey;

    setGroupPage(1);
    fetchGroupChatsData(1, debouncedSearchQuery, true);
  }, [activeTab, debouncedSearchQuery, fetchGroupChatsData]);

  // Load more group chats
  const handleShowMoreGroups = () => {
    const nextPage = groupPage + 1;
    setGroupPage(nextPage);
    fetchGroupChatsData(nextPage, debouncedSearchQuery);
  };

  // Use conversation actions hook
  const {
    toggleStar,
    moveToProject,
    moveSingleConversation,
    renameConversation,
    deleteConversation,
  } = useConversationActions({
    onRefresh: refreshConversations,
    onCacheClear: () => setSidebarProjectConversationsCache({}),
  });
  const updateConversationInList = useConversationsStore(
    state => state.updateConversationInList
  );

  // Load more (Show more button)
  const handleShowMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchConversations(nextPage, debouncedSearchQuery);
  };

  // Toggle selection
  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Batch select (for box selection)
  const handleBatchSelect = useCallback((ids: string[]) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      ids.forEach(id => newSet.add(id));
      return newSet;
    });
  }, []);

  // Select all (🔥 过滤掉 viewer 角色的对话)
  const handleSelectAll = () => {
    const currentList =
      activeTab === 'chats' ? conversations : groupConversations;

    // 过滤掉 viewer 角色的对话
    const selectableConversations = currentList.filter(c => {
      const conversationProject = c.projectId
        ? projects.find(p => p.id === c.projectId)
        : null;
      const projectRole = conversationProject?.role || null;
      // 只包含非 viewer 角色的对话
      return projectRole !== 'viewer';
    });

    if (
      selectedIds.size === selectableConversations.length &&
      selectableConversations.length > 0
    ) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(
        new Set(selectableConversations.map(c => c.conversationId))
      );
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Batch share selected conversations
  const handleBatchShare = () => {
    if (selectedIds.size === 0) return;

    // 获取选中的会话信息
    const selectedConversations = currentConversations.filter(conv =>
      selectedIds.has(conv.conversationId)
    );

    const conversationIds = selectedConversations.map(c => c.conversationId);
    const conversationTitles = selectedConversations.map(c => c.title);

    // 打开批量分享弹窗
    setBatchShareModal({
      isOpen: true,
      conversationIds,
      conversationTitles,
    });
  };

  // Delete selected conversations
  const handleDelete = () => {
    if (selectedIds.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const res: any = await apiDelete('/api/ai/conversations/batch', {
        conversationIds: Array.from(selectedIds),
        _method: 'DELETE',
      });
      if (res?.code === 200) {
        showToast(t('dialog.successful'), 'success', {
          title: t('common.success'),
        });
        // Remove from global store for sidebar
        selectedIds.forEach(id => removeConversation(id));
        setSelectedIds(new Set());
        setShowBulkDeleteModal(false);
        setSidebarProjectConversationsCache({});
        await refreshConversations();
        // 🔥 刷新侧边栏 Recent Chats 和 Starred 列表
        window.refreshSidebarRecent?.();
        window.refreshSidebarStarred?.();
      }
    } catch (error) {
      showToast(t('errors.failedToDeleteConversations'), 'error', {
        title: t('common.error'),
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Confirm remove from project (single conversation)
  const confirmRemoveFromProject = async () => {
    if (!removeFromProjectConversationId) return;
    setIsRemovingFromProject(true);
    try {
      const moveSuccess = await moveSingleConversation(
        removeFromProjectConversationId,
        null
      );
      if (moveSuccess) {
        updateConversationInList(removeFromProjectConversationId, c => ({
          ...c,
          projectId: null,
        }));
        setConversations(prev =>
          prev.map(c =>
            c.conversationId === removeFromProjectConversationId
              ? { ...c, projectId: null }
              : c
          )
        );
      }
      setShowRemoveFromProjectModal(false);
      setRemoveFromProjectConversationId(null);
    } catch (error) {
      showToast(t('errors.generic'), 'error', {
        title: t('common.error'),
      });
    } finally {
      setIsRemovingFromProject(false);
    }
  };

  // Move selected conversations to project
  const handleMoveToProject = async () => {
    // 支持两种来源：右键菜单（moveFromMenuId）和批量选择（selectedIds）
    const idsToMove = moveFromMenuId
      ? [moveFromMenuId]
      : Array.from(selectedIds);
    if (idsToMove.length === 0 || !selectedMoveProjectId) return;

    setIsMoving(true);
    try {
      const success = await moveToProject(idsToMove, selectedMoveProjectId);
      if (success) {
        // Update global store for all moved conversations
        idsToMove.forEach(id => {
          updateConversationInList(id, c => ({
            ...c,
            projectId: selectedMoveProjectId,
          }));
        });
        setSelectedIds(new Set());
        setMoveFromMenuId(null);
        setShowMoveModal(false);
        setSelectedMoveProjectId(null);
        setMoveSearchQuery('');
        // Auto-switch client if current/default client is 'common'
        try {
          const { ensureNonCommonSelected } = await import('@/lib/agentUtils');
          ensureNonCommonSelected();
        } catch (e) {
          console.warn('Auto-switch agent after moveToProject failed', e);
        }
      }
    } finally {
      setIsMoving(false);
    }
  };

  const handleConversationSelect = (conversationId: string) => {
    if (selectedIds.size > 0) return; // Don't navigate if in selection mode
    if (onConversationSelect) {
      onConversationSelect(conversationId);
    }
  };

  // Handle menu actions from ConversationListPanel
  const handleMenuAction = useCallback(
    async (action: string, conversationId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      const conversation = conversations.find(
        c => c.conversationId === conversationId
      );
      if (!conversation) return;

      switch (action) {
        case 'select':
          toggleSelect(conversationId, event);
          break;
        case 'star':
          {
            const starSuccess = await toggleStar(
              conversationId,
              conversation.starred || false
            );
            if (starSuccess) {
              // Update global store for sidebar
              updateConversationInList(conversationId, c => ({
                ...c,
                starred: !c.starred,
              }));
            }
          }
          break;
        case 'rename':
          setRenameConversationId(conversationId);
          setRenameValue(conversation.title);
          setShowRenameModal(true);
          break;
        case 'share':
          setShareModal({
            isOpen: true,
            conversationId: conversation.conversationId,
            conversationTitle: conversation.title,
          });
          break;
        case 'changeProject':
          setMoveFromMenuId(conversationId);
          setShowMoveModal(true);
          break;
        case 'removeFromProject':
          setRemoveFromProjectConversationId(conversationId);
          setShowRemoveFromProjectModal(true);
          break;
        case 'addToProject':
          setMoveFromMenuId(conversationId);
          setShowMoveModal(true);
          break;
        case 'delete':
          setDeleteConversationId(conversationId);
          setShowDeleteModal(true);
          break;
      }
    },
    [
      conversations,
      toggleStar,
      moveSingleConversation,
      updateConversationInList,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ]
  );

  const formatConversationDate = (date: string) => {
    const now = new Date();
    const targetDate = new Date(date);

    // Normalize dates to midnight for accurate day comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate()
    );

    // Calculate day difference
    const ONE_DAY = 1000 * 60 * 60 * 24;
    const diffDays = Math.round((today.getTime() - target.getTime()) / ONE_DAY);

    // Today
    if (diffDays === 0) {
      return t('chatsPage.today');
    }

    // Yesterday
    if (diffDays === 1) {
      return t('chatsPage.yesterday');
    }

    // Within 30 days
    if (diffDays >= 2 && diffDays < 30) {
      return t('chatsPage.daysAgo', { count: diffDays });
    }

    // Older than 30 days: show month and day
    return targetDate.toLocaleDateString(
      i18nInstance.language === 'zh' ? 'zh-CN' : 'en-US',
      {
        month: 'short',
        day: 'numeric',
      }
    );
  };
  const filteredProjects = useMemo(() => {
    const trimmedQuery = moveSearchQuery.trim().toLowerCase();
    return projects.filter(
      p =>
        p.role !== 'viewer' &&
        !p.archived &&
        p.name.toLowerCase().includes(trimmedQuery)
    );
  }, [projects, moveSearchQuery]);

  // Calculate unread group chats count
  const unreadGroupCount = useMemo(() => {
    return groupConversations.filter(conv => !conv.lastVisitedAt).length;
  }, [groupConversations]);

  // Mark all group chats as read handler
  const handleMarkAllGroupRead = useCallback(async () => {
    if (unreadGroupCount === 0) return;
    setIsMarkingAllRead(true);
    try {
      const res = await apiPost<any>('/api/ai/conversations/group/visit', {});
      if (res?.code === 200) {
        // Refresh the list to update lastVisitedAt
        groupFetchedRef.current = null;
        await fetchGroupChatsData(1, debouncedSearchQuery, true);
        // Also refresh sidebar badge - refetch group chats to update unread count
        await window.refreshSidebarGroupChats?.();
        showToast(t('toast.allMarkedAsRead'), 'success');
      }
    } catch (err) {
      showToast(t('toast.failedToMarkAllAsRead'), 'error');
    } finally {
      setIsMarkingAllRead(false);
    }
  }, [unreadGroupCount, fetchGroupChatsData, debouncedSearchQuery, t]);

  // Get current list based on active tab
  // 🔥 修复：从 activeStreamingMessages 合并 isLoading 状态，使 Chats 页面显示对话的 loading 图标
  const currentConversations = useMemo(() => {
    const baseConversations =
      activeTab === 'chats' ? conversations : groupConversations;
    return (
      baseConversations
        // 🔥 过滤掉 archived 项目的 conversation
        .filter(conv => {
          if (conv.projectId) {
            const project = projects.find(p => p.id === conv.projectId);
            if (project?.archived) return false;
          }
          return true;
        })
        .map(conv => ({
          ...conv,
          isLoading:
            (
              activeStreamingMessages[conv.conversationId] ||
              activeStreamingMessages[conv.id] ||
              []
            ).length > 0,
        }))
    );
  }, [
    activeTab,
    conversations,
    groupConversations,
    activeStreamingMessages,
    projects,
  ]);
  const currentTotalCount =
    activeTab === 'chats' ? totalCount : groupTotalCount;
  const currentIsLoading = activeTab === 'chats' ? isLoading : isGroupLoading;
  const currentHasMore = activeTab === 'chats' ? hasMore : hasMoreGroups;
  const handleCurrentShowMore =
    activeTab === 'chats' ? handleShowMore : handleShowMoreGroups;

  return (
    <div className="mx-auto mt-0 sm:mt-4 w-full h-full min-h-0 px-4 md:px-8 lg:mt-6 max-w-4xl flex flex-col">
      {/* Mobile Header */}
      <MobileHeader className="-mx-4 md:-mx-8">
        <h3 className="text-base font-bold">{t('chatsPage.title')}</h3>
      </MobileHeader>
      {/* Header with New Chat button or Mark all read button */}
      <div className="flex items-center justify-end lg:justify-between py-1 sm:py-2 mt-1 sm:mt-8 sm:ml-6 font-medium flex-shrink-0 gap-3">
        <h1 className="text-2xl  text-gray-900 hidden lg:block">
          {t('chatsPage.title')}
        </h1>
        {activeTab === 'chats' ? (
          <button
            onClick={onNewChat}
            className="hidden sm:flex items-center gap-1 px-2.5 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus size={16} />
            <span className="font-medium text-sm">
              {t('chatsPage.newChat')}
            </span>
          </button>
        ) : (
          <button
            onClick={handleMarkAllGroupRead}
            disabled={unreadGroupCount === 0 || isMarkingAllRead}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
              unreadGroupCount === 0 || isMarkingAllRead
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
            title={
              unreadGroupCount === 0
                ? t('chatsPage.noUnreadItems')
                : t('chatsPage.markAsRead', { count: unreadGroupCount })
            }
          >
            <CheckCheck size={16} />
            <span>
              {isMarkingAllRead
                ? t('chatsPage.marking')
                : t('chatsPage.markAllRead')}
            </span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 sm:ml-6 border-b border-gray-200 flex-shrink-0">
        <button
          onClick={() => handleTabChange('chats')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'chats'
              ? 'text-gray-900 border-gray-900'
              : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          {t('chatsPage.title')}
          {totalCount > 0 && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === 'chats'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {totalCount}
            </span>
          )}
        </button>
        {/* <button
          onClick={() => handleTabChange('groupChats')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px disabled ${
            activeTab === 'groupChats'
              ? 'text-gray-900 border-gray-900'
              : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <UsersRound size={16} />
          Group Chats
          {groupTotalCount > 0 && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === 'groupChats'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {groupTotalCount}
            </span>
          )}
        </button> */}
      </div>

      {/* Search Bar with Time Filter */}
      <div className="py-3 sm:ml-6 flex-shrink-0 relative">
        <div className="flex items-center gap-2">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder={
                activeTab === 'chats'
                  ? t('chatsPage.searchChats')
                  : t('chatsPage.searchGroupChats')
              }
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b67b2b3] focus:border-transparent text-sm"
            />
          </div>

          {/* Time Filter Button */}
          <div ref={timePanelRef}>
            <button
              onClick={() => setShowTimePanel(!showTimePanel)}
              className={`flex items-center gap-1.5 px-3 py-2.5 border rounded-md text-sm transition-colors whitespace-nowrap ${
                startDate || endDate
                  ? 'border-blue-400 bg-blue-50 text-blue-700'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
              title={t('chatsPage.timeFilter')}
            >
              <Calendar size={16} />
              <Filter size={14} />
              {(startDate || endDate) && (
                <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
              )}
            </button>

            {/* Time Filter Panel */}
            {showTimePanel && (
              <div className="absolute left-0 right-0 sm:left-auto sm:right-0 top-full mt-2 sm:w-[420px] bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4 sm:p-5">
                {/* Time Type Selection */}
                <div className="flex items-center gap-4 sm:gap-6 mb-3 sm:mb-4">
                  <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer text-xs sm:text-sm font-medium">
                    <input
                      type="radio"
                      name="timeType"
                      checked={timeFilterType === 'updatedAt'}
                      onChange={() => setTimeFilterType('updatedAt')}
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 accent-gray-900"
                    />
                    <span>{t('chatsPage.updateTime')}</span>
                  </label>
                  <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer text-xs sm:text-sm font-medium">
                    <input
                      type="radio"
                      name="timeType"
                      checked={timeFilterType === 'createdAt'}
                      onChange={() => setTimeFilterType('createdAt')}
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 accent-gray-900"
                    />
                    <span>{t('chatsPage.createTime')}</span>
                  </label>
                </div>

                {/* Date Range Inputs */}
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <DatePicker
                    value={startDate}
                    onChange={setStartDate}
                    placeholder={t('chatsPage.startDate')}
                    className="flex-1"
                  />
                  <span className="text-gray-400 text-xs sm:text-sm font-medium flex-shrink-0">
                    —
                  </span>
                  <DatePicker
                    value={endDate}
                    onChange={setEndDate}
                    placeholder={t('chatsPage.endDate')}
                    className="flex-1"
                    align="right"
                  />
                </div>

                {/* Date Validation Error */}
                {startDate && endDate && startDate > endDate && (
                  <div className="text-xs text-red-500 mb-2 -mt-2">
                    {t('chatsPage.dateValidationError')}
                  </div>
                )}

                {/* Quick Selection Buttons */}
                <div className="flex items-center flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  <button
                    onClick={() => handleQuickSelect('thisWeek')}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm border border-gray-300 rounded-md hover:bg-gray-100 hover:border-gray-400 transition-colors"
                  >
                    {t('chatsPage.thisWeek')}
                  </button>
                  <button
                    onClick={() => handleQuickSelect('lastWeek')}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm border border-gray-300 rounded-md hover:bg-gray-100 hover:border-gray-400 transition-colors"
                  >
                    {t('chatsPage.lastWeek')}
                  </button>
                  <button
                    onClick={() => handleQuickSelect('thisMonth')}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm border border-gray-300 rounded-md hover:bg-gray-100 hover:border-gray-400 transition-colors"
                  >
                    {t('chatsPage.thisMonth')}
                  </button>
                  <button
                    onClick={() => handleQuickSelect('lastMonth')}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm border border-gray-300 rounded-md hover:bg-gray-100 hover:border-gray-400 transition-colors"
                  >
                    {t('chatsPage.lastMonth')}
                  </button>
                </div>

                {/* Clear and Apply */}
                <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                    }}
                    disabled={!startDate && !endDate}
                    className={`px-3 py-1.5 text-sm transition-colors ${
                      !startDate && !endDate
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('chatsPage.clearFilter')}
                  </button>
                  <button
                    onClick={() => {
                      setShowTimePanel(false);
                      // 🔥 取消所有选中 + 退出选择模式
                      setSelectedIds(new Set());
                      // 🔥 更新已应用的时间筛选 ref
                      appliedTimeRef.current = {
                        type: timeFilterType,
                        param: buildTimeParam(),
                      };
                      // 触发搜索：重置 fetchedRef 以强制重新加载
                      fetchedRef.current = null;
                      setPage(1);
                      fetchConversations(1, debouncedSearchQuery, true);
                    }}
                    disabled={!!(startDate && endDate && startDate > endDate)}
                    className={`px-5 py-1.5 text-sm rounded-lg transition-colors ${
                      startDate && endDate && startDate > endDate
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {t('chatsPage.apply')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Clear Button - 清除所有搜索条件并查询全部 */}
          <button
            onClick={() => {
              // 🔥 清除关键字
              setSearchQuery('');
              setDebouncedSearchQuery('');
              // 🔥 清除日期筛选
              setStartDate('');
              setEndDate('');
              setTimeFilterType('updatedAt');
              // 🔥 重置已应用的时间筛选 ref
              appliedTimeRef.current = { type: 'updatedAt', param: null };
              // 🔥 取消所有选中 + 退出选择模式
              setSelectedIds(new Set());
              // 🔥 触发无条件查询
              fetchedRef.current = null;
              setPage(1);
              fetchConversations(1, '', true);
            }}
            disabled={
              !searchQuery &&
              !startDate &&
              !endDate &&
              !appliedTimeRef.current.param
            }
            className={`flex items-center gap-1 px-3 py-2.5 border rounded-lg text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
              !searchQuery &&
              !startDate &&
              !endDate &&
              !appliedTimeRef.current.param
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400'
            }`}
          >
            <X size={15} />
            {t('chatsPage.clearFilter')}
          </button>
        </div>
      </div>
      {/* Selection toolbar or Chat count */}
      {selectedIds.size > 0 ? (
        <div className="flex items-center justify-between py-2 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
            >
              <div className="shrink-0 w-4 h-4 flex items-center justify-center rounded cursor-pointer bg-[#2c84db]">
                {selectedIds.size === currentConversations.length ? (
                  <svg
                    className="w-2.5 h-2.5 text-white"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg
                    className="w-2.5 h-2.5 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M2 12H20"
                      strokeWidth="2.5"
                      stroke="currentColor"
                      strokeLinecap="round"
                    ></path>
                  </svg>
                )}
              </div>
              <span className="font-normal text-gray-700 ml-2">
                {t('chatsPage.selected', { count: selectedIds.size })}
              </span>
            </button>
            <button
              onClick={() => setShowMoveModal(true)}
              className="p-2 hover:bg-[#f0f1ed] rounded transition-colors"
              title={t('tooltips.moveToProject')}
            >
              <GalleryVerticalEnd size={20} className="text-gray-600" />
            </button>
            <button
              onClick={handleBatchShare}
              className="p-2 hover:bg-[#f0f1ed] rounded transition-colors"
              title={t('tooltips.batchShare') || 'Batch Share'}
            >
              <Share2 size={20} className="text-gray-600" />
            </button>
            <button
              onClick={handleDelete}
              className="p-2 hover:bg-[#f0f1ed] rounded transition-colors"
              title={t('common.delete')}
            >
              <Trash2 size={20} className="text-gray-600" />
            </button>
          </div>
          <button
            onClick={clearSelection}
            className="p-1.5 hover:bg-gray-300 rounded transition-colors"
            title={t('tooltips.clearSelection')}
          >
            <X size={18} className="text-gray-700" />
          </button>
        </div>
      ) : (
        <div className="sm:ml-8 py-2 flex-shrink-0">
          {activeTab !== 'groupChats' && (
            <p className="text-sm text-gray-600">
              {currentTotalCount || currentConversations.length}{' '}
              {(currentTotalCount || currentConversations.length) <= 1
                ? t('chatsPage.chat')
                : t('chatsPage.chats')}
              {(currentTotalCount || currentConversations.length) > 0 && (
                <button
                  onClick={handleSelectAll}
                  className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
                >
                  {t('chatsPage.select')}
                </button>
              )}
            </p>
          )}
        </div>
      )}
      {/* Conversations List */}
      <ConversationListPanel
        conversations={currentConversations}
        isLoading={currentIsLoading}
        searchQuery={searchQuery}
        selectedIds={selectedIds}
        projects={projects}
        onConversationSelect={handleConversationSelect}
        onToggleSelect={toggleSelect}
        onMenuAction={handleMenuAction}
        onShowMore={handleCurrentShowMore}
        hasMore={currentHasMore}
        showProjectMenu={activeTab === 'chats'}
        scrollContainerRef={scrollContainerRef}
        formatDate={formatConversationDate}
        onBatchSelect={handleBatchSelect}
        disableSelection={activeTab === 'groupChats'}
        currentUserEmail={userInfo?.email}
      />

      {/* Rename Chat Modal */}
      <RenameDialog
        isOpen={showRenameModal}
        title={t('dialog.renameConversation')}
        currentName={renameValue}
        isLoading={isRenaming}
        onConfirm={async (newName: string) => {
          if (containsXSS(newName)) {
            showToast(t('errors.invalidInput'), 'warning');
            return;
          }
          if (renameConversationId) {
            setIsRenaming(true);
            try {
              const result = await renameConversation(
                renameConversationId,
                newName
              );
              if (result?.code === 200) {
                updateConversationInList(renameConversationId, c => ({
                  ...c,
                  title: newName,
                }));
                setShowRenameModal(false);
                setRenameConversationId(null);
                setRenameValue('');
              }
            } finally {
              setIsRenaming(false);
            }
          }
        }}
        onCancel={() => {
          if (isRenaming) return;
          setShowRenameModal(false);
          setRenameConversationId(null);
          setRenameValue('');
        }}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConversationDialog
        isOpen={showDeleteModal}
        onConfirm={async () => {
          if (deleteConversationId) {
            const res: any = await deleteConversation(deleteConversationId);
            if (res?.code === 200) {
              removeConversation(deleteConversationId);
              setDeleteConversationId(null);
            }
            return res;
          }
        }}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteConversationId(null);
        }}
      />

      {/* Move to Project Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                  {t('chatsPage.moveChat')}
                </h2>
                <p className="text-sm text-gray-600">
                  {t('chatsPage.moveChatDescription')}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setSelectedMoveProjectId(null);
                  setMoveSearchQuery('');
                  setMoveFromMenuId(null);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            <div className="px-6 pb-6">
              <div className="relative mb-4">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder={t('form.searchOrCreateProject')}
                  value={moveSearchQuery}
                  onChange={e => setMoveSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                />
              </div>

              <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-md bg-gray-50">
                {filteredProjects.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    {t('chatsPage.noProjectsFound')}
                  </div>
                ) : (
                  filteredProjects.map(
                    (project: { id: string; name: string }) => (
                      <div
                        key={project.id}
                        onClick={() => setSelectedMoveProjectId(project.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors text-left border-b border-gray-200 last:border-b-0 ${
                          selectedMoveProjectId === project.id
                            ? 'bg-blue-50'
                            : 'bg-white hover:bg-gray-100'
                        }`}
                      >
                        <div
                          className={`shrink-0 w-4 h-4 flex items-center justify-center rounded-full border-2 transition-colors ${
                            selectedMoveProjectId === project.id
                              ? 'border-[#2c84db] bg-[#2c84db]'
                              : 'border-gray-300 bg-white'
                          }`}
                        >
                          {selectedMoveProjectId === project.id && (
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-900 truncate">
                          {project.name}
                        </span>
                      </div>
                    )
                  )
                )}
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => {
                    if (isMoving) return;
                    setShowMoveModal(false);
                    setSelectedMoveProjectId(null);
                    setMoveSearchQuery('');
                    setMoveFromMenuId(null);
                  }}
                  disabled={isMoving}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleMoveToProject}
                  disabled={isMoving || !selectedMoveProjectId}
                  className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isMoving && (
                    <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                  )}
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showBulkDeleteModal}
        title={t('dialog.deleteConversation')}
        message={t('confirmDialog.deleteConversation', {
          title: t('common.selected') + ' ' + selectedIds.size,
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isBulkDeleting}
        onConfirm={confirmBulkDelete}
        onCancel={() => {
          if (isBulkDeleting) return;
          setShowBulkDeleteModal(false);
        }}
      />

      {/* Remove from Project Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRemoveFromProjectModal}
        title={t('chat.removeFromProject')}
        message={t('confirmDialog.removeFromProjectConfirm', { count: 1 })}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        type="warning"
        isLoading={isRemovingFromProject}
        onConfirm={confirmRemoveFromProject}
        onCancel={() => {
          if (isRemovingFromProject) return;
          setShowRemoveFromProjectModal(false);
          setRemoveFromProjectConversationId(null);
        }}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={() =>
          setShareModal({
            isOpen: false,
            conversationId: '',
            conversationTitle: '',
          })
        }
        conversationId={shareModal.conversationId}
        conversationTitle={shareModal.conversationTitle}
      />

      {/* Batch Share Modal */}
      <BatchShareModal
        isOpen={batchShareModal.isOpen}
        onClose={() =>
          setBatchShareModal({
            isOpen: false,
            conversationIds: [],
            conversationTitles: [],
          })
        }
        conversationIds={batchShareModal.conversationIds}
        conversationTitles={batchShareModal.conversationTitles}
        onSuccess={() => {
          setBatchShareModal({
            isOpen: false,
            conversationIds: [],
            conversationTitles: [],
          });
          clearSelection();
          showToast(t('toast.batchShareSuccess') || '批量分享成功', 'success');
        }}
      />
    </div>
  );
}
